import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useLocalStorage } from './useLocalStorage';
import useLoader from './useLoader';
import { toast } from 'react-toastify';
import { handleError } from '../util/handleError';
import { ListApiResponse } from '../types/apiResponse';

type TableSettings = {
  sortedField: {
    key: string;
    direction: 'asc' | 'desc';
  };
  pageNumber: number;
  pageSize: number;
  columns: string[];
  filters: Record<string, string>;
  scopes: Record<string, string>;
};

/**
 * Custom hook for managing table settings with pagination, sorting, and filtering
 *
 * @template TItem The type of items in the table data
 * @template TParams The type of additional parameters for the data loading function
 *
 * @example
 * // Define your types for better type checking
 * interface User {
 *   id: string;
 *   fullName: string;
 *   email: string;
 *   role: string;
 * }
 *
 * // In your component:
 * const {
 *   tableSettings,
 *   isLoading,
 *   refreshData,
 *   sortBy,
 *   setPage,
 *   setPageSize
 * } = useTableSettings<User, { id: string }>(
 *   'companyuserlist-2.14-settings',
 *   getTableSettingsDefaults({
 *     defaultSort: 'fullName',
 *     defaultVisibleColumns: ['fullName', 'email', 'role'],
 *   }),
 *   { id: companyId },  // Additional params for the API call
 *   loadUsers,          // Function to load users from API
 *   setUsers,           // State setter for users
 *   reload,             // Function to trigger UI reload
 *   t('Failed to load company users')  // Error message
 * );
 *
 */
const useTableSettings = <TItem = unknown, TParams = unknown>(
  key: string,
  defaults: TableSettings,
  parameters: TParams,
  loadList: (
    params: TParams & {
      filters: Record<string, string>;
      _scopes: Record<string, string>;
      _sort: string;
      _order: 'asc' | 'desc';
      _page: number;
      _limit: number;
    }
  ) => Promise<ListApiResponse<TItem>>,
  setList: (items: ListApiResponse<TItem>) => void,
  reload?: Date | undefined,
  errorMessage?: string,
  defaultScope?: Record<string, string>
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isInit, setIsInit] = useState(true);
  const { isLoading, start: startLoader, clear: clearLoader } = useLoader();
  const [tableSettings, setTableSettings] = useLocalStorage(key, defaults);
  const reloadRef = useRef(reload);
  const searchRef = useRef(location.search);
  /** When true, filterChange just ran and did its own reload; skip the URL-sync effect once to avoid overwriting with stale URL. */
  const filterChangeInProgressRef = useRef(false);

  const refParameters = useRef(parameters);

  const checkPageResult = useCallback(
    (pageNumber: number) => {
      pageNumber--;
      if (pageNumber >= 0 && pageNumber !== tableSettings.pageNumber) {
        setTableSettings({
          ...tableSettings,
          pageNumber: pageNumber,
        });
      }
    },
    [tableSettings, setTableSettings]
  );

  const getFilters = useCallback(
    (prefix: string) => {
      const params = new URLSearchParams(location.search);
      let filters = {};
      for (let key of params.keys()) {
        if (key.startsWith(prefix)) {
          filters = {
            ...filters,
            [key.substring(2)]: params.get(key),
          };
        }
      }
      return filters;
    },
    [location.search]
  );

  const reloadTableData = useCallback(
    async (newTableSettings: TableSettings) => {
      startLoader();

      try {
        const scopes =
          Object.keys(newTableSettings.scopes).length === 0
            ? defaultScope || {}
            : newTableSettings.scopes;
        const response = await loadList({
          ...{
            filters: newTableSettings.filters,
            _scopes: scopes,
            _sort: newTableSettings.sortedField.key,
            _order: newTableSettings.sortedField.direction,
            _page: newTableSettings.pageNumber,
            _limit: newTableSettings.pageSize,
          },
          ...refParameters.current,
        });

        setList(response);
        checkPageResult(response.page);
      } catch (e) {
        handleError(e, navigate, () => toast.error(errorMessage));
      } finally {
        clearLoader();
      }
    },
    [
      checkPageResult,
      clearLoader,
      defaultScope,
      errorMessage,
      loadList,
      navigate,
      setList,
      startLoader,
    ]
  );

  useEffect(() => {
    // Store the reload in a ref so we can compare it
    if (reloadRef.current !== reload) {
      reloadTableData(tableSettings);
      reloadRef.current = reload;
    }
  }, [reload, reloadTableData, tableSettings]);

  const handleSetTableSettings = useCallback(
    (newTableSettings: TableSettings) => {
      reloadTableData(newTableSettings);
      setTableSettings(newTableSettings);
    },
    [reloadTableData, setTableSettings]
  );

  // Handles initial load and URL changes (e.g. browser back/forward). When the user
  // changes a filter via filterChange we reload there and set filterChangeInProgressRef
  // so this effect does not overwrite with stale URL on the next run.
  useEffect(() => {
    if (isInit || searchRef.current !== location.search) {
      if (filterChangeInProgressRef.current) {
        filterChangeInProgressRef.current = false;
        searchRef.current = location.search;
        setIsInit(false);
        return;
      }
      searchRef.current = location.search;
      const getUrlFilters = () => {
        const params = new URLSearchParams(location.search);
        let filters = {};
        for (let key of params.keys()) {
          if (key.startsWith('f_')) {
            filters = {
              ...filters,
              [key.substring(2)]: params.get(key),
            };
          }
        }
        return filters;
      };

      const getUrlScopes = () => {
        const params = new URLSearchParams(location.search);
        let scopes = {};
        for (let key of params.keys()) {
          if (key.startsWith('s_')) {
            scopes = {
              ...scopes,
              [key.substring(2)]: params.get(key),
            };
          }
        }
        return scopes;
      };

      const newTableSettings = {
        ...tableSettings,
        filters: getUrlFilters(),
        scopes: getUrlScopes(),
      } as TableSettings;

      handleSetTableSettings(newTableSettings);
      setIsInit(false);
    }
  }, [
    isInit,
    tableSettings,
    setTableSettings,
    location,
    reloadTableData,
    handleSetTableSettings,
  ]);

  const filterChange = useCallback(
    (key: string, value: string) => {
      const currentValue = tableSettings.filters
        ? tableSettings.filters[key]
        : '';
      if (currentValue !== value) {
        const params = new URLSearchParams(location.search);
        if (value === '' || value === null) {
          params.delete(`f_${key}`);
        } else {
          params.set(`f_${key}`, value);
        }
        const newTableSettings: TableSettings = {
          ...tableSettings,
          filters: { ...tableSettings.filters, [key]: value },
          pageNumber: 0,
        };
        filterChangeInProgressRef.current = true;
        setTableSettings(newTableSettings);
        reloadTableData(newTableSettings);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    },
    [
      tableSettings,
      location.search,
      location.pathname,
      navigate,
      setTableSettings,
      reloadTableData,
    ]
  );

  const filtersChange = useCallback(
    (filters: { key: string; value: string }[]) => {
      const params = new URLSearchParams(location.search);
      const newFilters = { ...tableSettings.filters };

      // Update URL parameters and collect filter changes
      filters.forEach(({ key, value }) => {
        const currentValue = tableSettings.filters
          ? tableSettings.filters[key]
          : '';
        if (currentValue !== value) {
          // Update URL params
          if (value === '' || value === null) {
            params.delete(`f_${key}`);
          } else {
            params.set(`f_${key}`, value);
          }
        }

        newFilters[key] = value;
      });

      const newTableSettings = {
        ...tableSettings,
        filters: newFilters,
        pageNumber: 0,
      };

      handleSetTableSettings(newTableSettings);
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
      });
    },
    [
      location.search,
      location.pathname,
      tableSettings,
      navigate,
      handleSetTableSettings,
    ]
  );

  return {
    tableSettings,
    filterChange,
    isLoading,
    parameters: refParameters,
    filtersChange,
    filters: getFilters('f_'),
    handleSetTableSettings,
  };
};

type GetTableSettingsDefaultsProps = {
  defaultSort: string;
  direction?: 'asc' | 'desc';
  pageSize?: number;
  defaultVisibleColumns: string[];
};

const getTableSettingsDefaults = ({
  defaultSort = '',
  direction = 'asc',
  pageSize = 10,
  defaultVisibleColumns = [],
}: GetTableSettingsDefaultsProps): TableSettings => {
  return {
    sortedField: {
      key: defaultSort,
      direction: direction,
    },
    pageNumber: 0,
    pageSize: pageSize,
    columns: defaultVisibleColumns,
    filters: {},
    scopes: {},
  };
};

const areTableSettingsEqual = (old: TableSettings, current: TableSettings) => {
  return (
    old.sortedField.key === current.sortedField.key &&
    old.sortedField.direction === current.sortedField.direction &&
    old.pageNumber === current.pageNumber &&
    old.pageSize === current.pageSize &&
    JSON.stringify(old.columns) === JSON.stringify(current.columns) &&
    JSON.stringify(old.filters) === JSON.stringify(current.filters) &&
    JSON.stringify(old.scopes) === JSON.stringify(current.scopes)
  );
};

export { useTableSettings, getTableSettingsDefaults };
